import { useState, useRef, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import * as IBAN from "iban";
import { getFirstOfMonthOnOrAfter } from "../../utils/dateUtils";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const formatNoticeDate = (isoDate: string) => {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

interface BillingInfo {
  email: string;
  iban: string;
  address: string;
  city: string;
  zip: string;
  country: string;
}

export interface SigningBillingInfo extends BillingInfo {
  vat_code: string;
  company_name: string;
}

export interface ContractSigningPayload {
  installation_id: string;
  signature_name: string;
  signee_function: string;
  signed_at: string;
  billing_info: SigningBillingInfo;
  company_id: number;
  company_name: string;
  site_name: string;
  start_date: string;
  end_date: string | null;
  partner_id: number;
  profit_share: string;
  monthly_fee: string;
  steering_fee_performance_pct: string;
  inverter_power: number;
  opportunity_cost: number;
  controller: string;
  injection_installation_id: string;
  offtake_installation_id?: string | null;
  signature_image?: string | null;
}

export interface BessOnboardingContractData {
  startDate: string;
  actualStart: string;
  profitShare: string;
  monthlyFee: string;
  partner: string;
  peakPower: string;
  inverterPower: string;
  controller: string;
  opportunityCost: string;
  validUntil: string;
  company: {
    name: string;
    address: string;
    vat: string;
    email: string;
    iban: string;
  };
  installationId: string;
  companyId: number;
  siteName: string;
  endDate: string | null;
  partnerId: number;
  profitShareValue: string;
  monthlyFeeValue: string;
  steeringFeePerformancePct: string;
  inverterPowerKva: number;
  opportunityCostValue: number;
  injectionInstallationId: string;
  offtakeInstallationId?: string | null;
}

interface ContractPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: (signatureData: ContractSigningPayload) => Promise<void>;
  pdfUrl?: string;
  signerName?: string;
  contractData: BessOnboardingContractData;
  hasCombinedServices?: boolean;
  signBlockedReason?: "INJECTION_UNSIGNED" | "OFFTAKE_UNSIGNED" | null;
  // Defaults to true (signing allowed). When false, the site has no certified controller
  // and signing is blocked with inline guidance, mirroring the backend 422 guard.
  hasCertifiedController?: boolean;
}

export const ContractPreviewModal = ({
  isOpen,
  onClose,
  onAccept,
  pdfUrl,
  signerName = "",
  contractData,
  hasCombinedServices = false,
  signBlockedReason = null,
  hasCertifiedController = true,
}: ContractPreviewModalProps) => {
  const { t } = useTranslation("bessOnboarding");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [showBillingEdit, setShowBillingEdit] = useState(false);
  const [showBillingExpanded, setShowBillingExpanded] = useState(false);
  const [ibanValid, setIbanValid] = useState<boolean | null>(null);

  // New state
  const [signerFunction, setSignerFunction] = useState("");
  const [signatureMode, setSignatureMode] = useState<"type" | "draw" | "upload">("type");
  const [canvasHasDrawing, setCanvasHasDrawing] = useState(false);
  const [uploadedSignature, setUploadedSignature] = useState<string | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [zoom, setZoom] = useState(1);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const viewedPagesRef = useRef<Set<number>>(new Set());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  const parseAddress = (address: string) => {
    const lines = address.split("\n");
    const streetLine = lines[0] || "";
    const cityLine = lines[1] || "";
    const zipMatch = cityLine.match(/^(\d+)\s+(.+)$/);
    return {
      street: streetLine,
      zip: zipMatch ? zipMatch[1] : "",
      city: zipMatch ? zipMatch[2] : cityLine,
    };
  };

  const addressParts = parseAddress(contractData.company.address);
  const [editableBillingInfo, setEditableBillingInfo] = useState<BillingInfo>({
    email: contractData.company.email,
    iban: contractData.company.iban,
    address: addressParts.street,
    city: addressParts.city,
    zip: addressParts.zip,
    country: "Belgium",
  });

  const validateIBAN = (value: string) => {
    const cleanIBAN = value.replace(/\s/g, "").toUpperCase();
    if (!cleanIBAN) { setIbanValid(null); return; }
    setIbanValid(IBAN.isValid(cleanIBAN));
  };

  const formatIBAN = (value: string) => {
    const cleanValue = value.replace(/\s/g, "").toUpperCase();
    return cleanValue.match(/.{1,4}/g)?.join(" ") || cleanValue;
  };

  const handleBillingInputChange = (field: keyof BillingInfo, value: string) => {
    if (field === "iban") {
      value = formatIBAN(value);
      validateIBAN(value);
    }
    setEditableBillingInfo((prev) => ({ ...prev, [field]: value }));
  };

  const validateEmail = (email: string) => {
    const emails = email.split(",").map((e) => e.trim()).filter(Boolean);
    return emails.length > 0 && emails.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  };

  useEffect(() => {
    if (isOpen) {

      setCurrentPage(1);
      setTotalPages(0);
      setHasAcceptedTerms(false);
      setPdfLoadError(null);
      setSignatureName("");
      setShowBillingEdit(false);
      setSignerFunction("");
      setSignatureMode("type");
      setCanvasHasDrawing(false);
      setUploadedSignature(null);
      setScrollProgress(0);
      setZoom(1);
      viewedPagesRef.current.clear();

      const ap = parseAddress(contractData.company.address);
      setEditableBillingInfo({
        email: contractData.company.email,
        iban: contractData.company.iban,
        address: ap.street,
        city: ap.city,
        zip: ap.zip,
        country: "Belgium",
      });

      if (contractData.company.iban) validateIBAN(contractData.company.iban);
    }
  }, [isOpen, contractData]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;

    const pct = Math.min(100, Math.round((scrollTop / (scrollHeight - clientHeight)) * 100));
    setScrollProgress(prev => Math.max(prev, isNaN(pct) ? 0 : pct));

    if (totalPages > 0) {
      const pageHeight = scrollHeight / totalPages;
      const visiblePage = Math.ceil((scrollTop + clientHeight / 2) / pageHeight);
      setCurrentPage(Math.min(visiblePage, totalPages));
      viewedPagesRef.current.add(visiblePage);
    }
  };

  const handleAcceptTermsChange = (checked: boolean) => {
    setHasAcceptedTerms(checked);
    if (checked && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: "smooth" });
      setTimeout(() => {
        setScrollProgress(100);
        for (let i = 1; i <= totalPages; i++) viewedPagesRef.current.add(i);
      }, 500);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    viewedPagesRef.current.add(1);
    setTimeout(() => {
      const container = scrollContainerRef.current;
      if (container && container.scrollHeight <= container.clientHeight) {
        setScrollProgress(100);
      }
    }, 100);
    setPdfLoadError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error("Error loading PDF:", error);
    setPdfLoadError("Failed to load PDF document. Please try again.");
  };

  // Canvas drawing handlers
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    isDrawingRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height),
    );
  };

  useEffect(() => {
    if (signatureMode !== "draw") return;

    const handleMove = (e: MouseEvent) => {
      if (!isDrawingRef.current || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.lineTo(
        (e.clientX - rect.left) * (canvas.width / rect.width),
        (e.clientY - rect.top) * (canvas.height / rect.height),
      );
      ctx.strokeStyle = "#04344d";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      setCanvasHasDrawing(true);
    };

    const handleUp = () => { isDrawingRef.current = false; };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [signatureMode]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    setCanvasHasDrawing(false);
  };

  const handleAcceptContract = async () => {
    if (!signerFunction.trim()) { alert("Please enter your function / role"); return; }
    if (!signerName && !signatureName.trim()) { alert("Please enter your full name"); return; }
    if (signatureMode === "type" && signerName && signatureName !== signerName) {
      alert(`The name must match exactly: ${signerName}`);
      return;
    }
    if (!hasAcceptedTerms) { alert(t("contractSign.consentRequired")); return; }
    if (scrollProgress < 80) { alert("Please review the entire contract before accepting"); return; }
    if (!ibanValid) { alert("Please enter a valid IBAN before signing"); return; }
    if (!editableBillingInfo.email || !validateEmail(editableBillingInfo.email)) {
      alert("Please enter a valid billing email address");
      return;
    }
    if (!editableBillingInfo.address || !editableBillingInfo.city || !editableBillingInfo.zip) {
      alert("Please complete all billing information fields");
      return;
    }

    setIsProcessing(true);
    try {
      const signingPayload: ContractSigningPayload = {
        installation_id: contractData.installationId,
        signature_name: signatureName.trim() || signerName,
        signee_function: signerFunction.trim(),
        signed_at: new Date().toISOString(),
        billing_info: {
          email: editableBillingInfo.email,
          iban: editableBillingInfo.iban.replace(/\s/g, ""),
          address: editableBillingInfo.address,
          city: editableBillingInfo.city,
          zip: editableBillingInfo.zip,
          country: editableBillingInfo.country,
          vat_code: contractData.company.vat,
          company_name: contractData.company.name,
        },
        company_id: contractData.companyId,
        company_name: contractData.company.name,
        site_name: contractData.siteName,
        start_date: contractData.actualStart,
        end_date: contractData.endDate,
        partner_id: contractData.partnerId,
        profit_share: contractData.profitShareValue,
        monthly_fee: contractData.monthlyFeeValue,
        steering_fee_performance_pct: contractData.steeringFeePerformancePct,
        inverter_power: contractData.inverterPowerKva,
        opportunity_cost: contractData.opportunityCostValue,
        controller: contractData.controller,
        injection_installation_id: contractData.injectionInstallationId,
        offtake_installation_id: contractData.offtakeInstallationId,
        signature_image: (() => {
          if (signatureMode === "draw") {
            return canvasRef.current?.toDataURL("image/png") ?? null;
          }
          if (signatureMode === "upload") {
            return uploadedSignature;
          }
          // Type mode — render cursive name to an off-screen canvas
          const offscreen = document.createElement("canvas");
          offscreen.width = 300;
          offscreen.height = 80;
          const ctx = offscreen.getContext("2d");
          if (ctx) {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, 300, 80);
            ctx.font = "italic 28px Georgia, serif";
            ctx.fillStyle = "#04344d";
            ctx.textBaseline = "middle";
            ctx.fillText(signatureName.trim() || signerName, 16, 40);
            return offscreen.toDataURL("image/png");
          }
          return null;
        })(),
      };
      await onAccept(signingPayload);
    } catch (error) {
      console.error("Error signing contract:", error);
      alert("Failed to sign contract. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    try {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `pv-curtailment-contract-${new Date().getTime()}.pdf`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      window.open(pdfUrl, "_blank");
    }
  };

  if (!isOpen) return null;

  // Informational only — the backend rolls a past start date forward at signing time; signing stays enabled.
  const todayStr = new Date().toISOString().split("T")[0];
  const actualStartDay = (contractData.actualStart || "").slice(0, 10);
  const startDatePassed = !!actualStartDay && actualStartDay < todayStr;

  // Derived sign-readiness
  const documentReviewed = scrollProgress >= 80;
  const functionFilled = signerFunction.trim().length > 0;
  const nameProvided = !!signerName || signatureName.trim().length > 0;
  const signatureValid =
    signatureMode === "type"
      ? (signerName ? signatureName === signerName : signatureName.trim().length > 0)
      : signatureMode === "draw"
      ? canvasHasDrawing
      : uploadedSignature !== null;
  const billingValid =
    ibanValid === true &&
    validateEmail(editableBillingInfo.email) &&
    !!editableBillingInfo.address &&
    !!editableBillingInfo.city &&
    !!editableBillingInfo.zip;
  const controllerMissing = hasCertifiedController === false;
  const controllerHint = controllerMissing ? t("controllerRequired.inlineHint") : "";
  const serverBlockedHint =
    signBlockedReason === "INJECTION_UNSIGNED"
      ? "The injection contract must be signed before this contract can be signed."
      : signBlockedReason === "OFFTAKE_UNSIGNED"
      ? "The offtake contract must be signed before this contract can be signed."
      : "";
  const canSign =
    !!pdfUrl &&
    documentReviewed && nameProvided && functionFilled && hasAcceptedTerms && signatureValid && billingValid && !isProcessing && !serverBlockedHint && !controllerMissing;

  const lockHint = !pdfUrl
    ? "Contract unavailable. Close this window and generate the contract again."
    : controllerHint
    ? controllerHint
    : serverBlockedHint
    ? serverBlockedHint
    : !documentReviewed
    ? "Scroll through the contract to unlock"
    : !nameProvided
    ? "Enter your full name above"
    : !functionFilled
    ? "Enter your function / role above"
    : !signatureValid
    ? signatureMode === "type"
      ? (signerName ? `Type your name exactly: ${signerName}` : "Enter your full name above")
      : signatureMode === "draw" ? "Draw your signature above" : "Upload a signature image"
    : !hasAcceptedTerms
    ? "Accept the terms and conditions"
    : !billingValid
    ? "Check billing information"
    : "";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1100px] flex flex-col" style={{ maxHeight: "95vh" }}>

        {/* ── Header ── */}
        <div className="bg-primary-950 px-5 py-3 flex items-center justify-between flex-shrink-0 rounded-t-xl">
          <div>
            <h2 className="text-sm font-semibold text-white">{hasCombinedServices ? "Yuso Flex Contract" : "BESS Contract"}</h2>
            <p className="text-xs text-primary-300 mt-0.5">
              {totalPages > 0 ? `Page ${currentPage} of ${totalPages}` : "Scroll through the contract to review before signing"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-white/10 border border-white/15 text-white hover:bg-white/20 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Split body ── */}
        <div className="flex flex-1 min-h-0">

          {/* ── Left: PDF panel ── */}
          <div className="flex-1 flex flex-col bg-gray-200 min-w-0">
            {/* Toolbar */}
            <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.25) * 4) / 4))}
                  className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-medium leading-none"
                  title="Zoom out"
                >−</button>
                <span className="text-xs text-gray-600 w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
                <button
                  onClick={() => setZoom(z => Math.min(2, Math.round((z + 0.25) * 4) / 4))}
                  className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-medium leading-none"
                  title="Zoom in"
                >+</button>
              </div>
              <button
                onClick={handleDownload}
                disabled={!pdfUrl}
                className="inline-flex items-center gap-1.5 text-xs text-gray-600 border border-gray-300 px-3 py-1 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download
              </button>
            </div>

            {/* Scrollable PDF */}
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-auto p-4 flex flex-col items-start gap-0"
            >
              {!pdfUrl ? (
                <div className="flex items-center justify-center h-full w-full">
                  <div className="rounded-lg bg-red-50 border border-red-100 p-5 text-center max-w-sm">
                    <p className="font-semibold text-red-800 mb-1">Contract preview is unavailable</p>
                    <p className="text-red-700 text-sm">
                      Please close this window and generate the contract again.
                    </p>
                  </div>
                </div>
              ) : pdfLoadError ? (
                <div className="flex items-center justify-center h-full">
                  <div className="rounded-lg bg-red-50 border border-red-100 p-5 text-center max-w-sm">
                    <p className="font-semibold text-red-800 mb-1">Failed to load contract</p>
                    <p className="text-red-700 text-sm">{pdfLoadError}</p>
                  </div>
                </div>
              ) : (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-400" />
                    </div>
                  }
                  className="flex flex-col items-center mx-auto"
                >
                  {totalPages > 0 &&
                    Array.from(new Array(totalPages), (_, index) => (
                      <div key={`page_${index + 1}`} className="mb-2">
                        <Page
                          pageNumber={index + 1}
                          width={Math.round(720 * zoom)}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          className="shadow-lg"
                        />
                      </div>
                    ))}
                </Document>
              )}
            </div>

            {/* Scroll hint */}
            <div className="bg-gray-300/80 border-t border-gray-300 text-center py-1.5 text-xs text-gray-600 flex-shrink-0 flex items-center justify-center gap-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
              </svg>
              Scroll down to read the full contract
            </div>
          </div>

          {/* ── Right: Sidebar (no overflow) ── */}
          <div className={`w-[290px] flex-shrink-0 flex flex-col border-l border-gray-200 bg-gray-50 ${showBillingEdit || showBillingExpanded ? "overflow-y-auto" : "overflow-hidden"}`}>

            {/* 1. Progress */}
            <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-primary-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                  </svg>
                  Document review
                </span>
                <span className={`text-xs font-semibold ${documentReviewed ? "text-green-600" : "text-primary-700"}`}>
                  {scrollProgress}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${documentReviewed ? "bg-green-600" : "bg-primary-600"}`}
                  style={{ width: `${scrollProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                {documentReviewed ? "✓ Document reviewed" : "Scroll to continue reading"}
              </p>
            </div>

            {/* 1. Contract Summary */}
            <div className="flex-shrink-0 px-3 py-2 bg-white border-b border-gray-200">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">Contract summary</p>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                  <span className="text-gray-400 flex-shrink-0">Site</span>
                  <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">{contractData.siteName}</span>
                </div>
                <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                  <span className="text-gray-400 flex-shrink-0">Start date</span>
                  <span className="text-gray-700 font-medium ml-2">{contractData.startDate}</span>
                </div>
                <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                  <span className="text-gray-400 flex-shrink-0">End date</span>
                  <span className="text-gray-700 font-medium ml-2">{contractData.validUntil}</span>
                </div>
                <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                  <span className="text-gray-400 flex-shrink-0">Fixed fee</span>
                  <span className="text-gray-700 font-medium ml-2">{contractData.monthlyFee}</span>
                </div>
                <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                  <span className="text-gray-400 flex-shrink-0">Profit share</span>
                  <span className="text-primary-700 font-semibold ml-2">{contractData.profitShare}</span>
                </div>
                <div className="flex justify-between px-2.5 py-1 text-[11px]">
                  <span className="text-gray-400 flex-shrink-0">Partner</span>
                  <span className="text-gray-700 font-medium ml-2">{contractData.partner}</span>
                </div>
              </div>
            </div>

            {/* 2. Signee */}
            <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">Signee</p>
              <div className="mb-2">
                <label className="font-medium text-gray-700 text-xs block mb-1">Full name</label>
                <input
                  type="text"
                  value={signerName || signatureName}
                  readOnly={!!signerName}
                  onChange={signerName ? undefined : (e) => setSignatureName(e.target.value)}
                  placeholder={signerName ? undefined : "Enter your full name"}
                  className={`rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs shadow-sm ${
                    signerName
                      ? "bg-gray-50 text-gray-500 cursor-default"
                      : "bg-white text-gray-700 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10"
                  }`}
                  disabled={isProcessing}
                />
              </div>
              <div>
                <label className="font-medium text-gray-700 text-xs block mb-1">
                  Function / Role <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={signerFunction}
                  onChange={(e) => setSignerFunction(e.target.value)}
                  placeholder="e.g. Director, CFO, Facility Manager"
                  className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10 bg-white text-gray-700 shadow-sm"
                  disabled={isProcessing}
                />
              </div>
            </div>

            {/* 3. Signature */}
            <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">Signature</p>
              {/* Mode tabs */}
              <div className="flex border border-gray-200 rounded-lg overflow-hidden mb-2">
                <button
                  onClick={() => setSignatureMode("type")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${signatureMode === "type" ? "bg-primary-700 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                >
                  Type
                </button>
                <button
                  onClick={() => setSignatureMode("draw")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${signatureMode === "draw" ? "bg-primary-700 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                >
                  Draw
                </button>
                <button
                  onClick={() => setSignatureMode("upload")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${signatureMode === "upload" ? "bg-primary-700 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                >
                  Upload
                </button>
              </div>

              {signatureMode === "type" ? (
                <div>
                  {signerName && (
                    <input
                      type="text"
                      value={signatureName}
                      onChange={(e) => setSignatureName(e.target.value)}
                      placeholder={`Type: ${signerName}`}
                      className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10 bg-white text-gray-700 shadow-sm mb-1.5"
                      disabled={isProcessing}
                    />
                  )}
                  <div className="h-12 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-primary-900 opacity-80" style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "18px" }}>
                      {signatureName || signerName}
                    </span>
                  </div>
                  {signerName && signatureName && signatureName !== signerName && (
                    <p className="text-[10px] text-red-600 mt-1">Must match exactly: {signerName}</p>
                  )}
                </div>
              ) : signatureMode === "draw" ? (
                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    width={258}
                    height={120}
                    onMouseDown={startDrawing}
                    className="border border-gray-200 rounded-lg bg-white cursor-crosshair w-full"
                    style={{ touchAction: "none" }}
                  />
                  {!canvasHasDrawing && (
                    <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-300 pointer-events-none">
                      Draw your signature here
                    </span>
                  )}
                  {canvasHasDrawing && (
                    <button
                      onClick={clearCanvas}
                      className="absolute bottom-1.5 right-2 text-[10px] text-gray-400 hover:text-gray-600"
                    >
                      Clear
                    </button>
                  )}
                </div>
              ) : (
                /* Upload tab */
                <div>
                  {uploadedSignature ? (
                    <div className="relative border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                      <img
                        src={uploadedSignature}
                        alt="Uploaded signature"
                        className="w-full h-[120px] object-contain p-2"
                      />
                      <button
                        onClick={() => setUploadedSignature(null)}
                        className="absolute top-1.5 right-2 text-[10px] text-gray-400 hover:text-gray-600 bg-white rounded px-1.5 py-0.5 border border-gray-200"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label
                      className="flex flex-col items-center justify-center h-[120px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 hover:border-gray-400 transition-colors"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
                          const reader = new FileReader();
                          reader.onload = (ev) => setUploadedSignature(ev.target?.result as string);
                          reader.readAsDataURL(file);
                        }
                      }}
                    >
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setUploadedSignature(ev.target?.result as string);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-400 mb-1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                      </svg>
                      <span className="text-xs text-gray-500 text-center px-2">Drop a PNG or JPG here,<br />or click to browse</span>
                    </label>
                  )}
                </div>
              )}
            </div>

            {/* 4. Billing */}
            <div className="flex-shrink-0 px-3 py-2 bg-white border-b border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowBillingExpanded(!showBillingExpanded)}
                  className="flex items-center gap-1.5 flex-1 min-w-0"
                >
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Billing information</p>
                  <svg
                    className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${showBillingExpanded ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  {!showBillingExpanded && (
                    <span className="text-[11px] text-gray-500 truncate ml-1">{contractData.company.name}</span>
                  )}
                </button>
                {showBillingExpanded && (
                  <button
                    onClick={() => setShowBillingEdit(!showBillingEdit)}
                    className="text-[11px] font-medium text-primary-700 hover:text-primary-800 flex-shrink-0 ml-2"
                  >
                    {showBillingEdit ? "Done" : "Edit"}
                  </button>
                )}
              </div>

              {showBillingExpanded && !showBillingEdit && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 mt-1.5">
                  <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                    <span className="text-gray-400 flex-shrink-0">Company</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">{contractData.company.name}</span>
                  </div>
                  <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                    <span className="text-gray-400 flex-shrink-0">Email</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">{editableBillingInfo.email}</span>
                  </div>
                  <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                    <span className="text-gray-400 flex-shrink-0">IBAN</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">{editableBillingInfo.iban}</span>
                  </div>
                  <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                    <span className="text-gray-400 flex-shrink-0">Address</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">{editableBillingInfo.address}</span>
                  </div>
                  <div className="flex justify-between px-2.5 py-1 text-[11px]">
                    <span className="text-gray-400 flex-shrink-0">City</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">{editableBillingInfo.city}, {editableBillingInfo.zip}</span>
                  </div>
                </div>
              )}

              {showBillingExpanded && showBillingEdit && (
                <div className="space-y-2 mt-1.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Billing Email</label>
                    <input
                      type="email"
                      multiple
                      value={editableBillingInfo.email}
                      onChange={(e) => handleBillingInputChange("email", e.target.value)}
                      className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                      placeholder="billing@company.com"
                    />
                    {editableBillingInfo.email && !validateEmail(editableBillingInfo.email) && (
                      <p className="text-xs text-red-600 mt-1">Invalid email format</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">IBAN</label>
                    <input
                      type="text"
                      value={editableBillingInfo.iban}
                      onChange={(e) => handleBillingInputChange("iban", e.target.value)}
                      className={`rounded-lg border h-8 block w-full px-3 text-xs focus:outline-none bg-white shadow-sm ${
                        editableBillingInfo.iban && !ibanValid ? "border-red-200" : "border-gray-200 focus:border-primary-600"
                      }`}
                      placeholder="BE00 0000 0000 0000"
                    />
                    {editableBillingInfo.iban && ibanValid === false && (
                      <p className="text-xs text-red-600 mt-1">Invalid IBAN format</p>
                    )}
                    {editableBillingInfo.iban && ibanValid === true && (
                      <p className="text-xs text-green-600 mt-1">✓ Valid IBAN</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Street Address</label>
                    <input
                      type="text"
                      value={editableBillingInfo.address}
                      onChange={(e) => handleBillingInputChange("address", e.target.value)}
                      className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                      placeholder="Street and Number"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      value={editableBillingInfo.city}
                      onChange={(e) => handleBillingInputChange("city", e.target.value)}
                      className="col-span-2 rounded-lg border border-gray-200 h-8 px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                      placeholder="City"
                    />
                    <input
                      type="text"
                      value={editableBillingInfo.zip}
                      onChange={(e) => handleBillingInputChange("zip", e.target.value)}
                      className="rounded-lg border border-gray-200 h-8 px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* 5. Footer */}
            <div className="flex-shrink-0 px-4 py-3 bg-white border-t border-gray-200 mt-auto">
              {startDatePassed && (
                <div className="mb-2.5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.732 0 2.815-1.874 1.95-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    {t("contractSign.startDatePassed", {
                      date: contractData.startDate,
                      effectiveDate: formatNoticeDate(getFirstOfMonthOnOrAfter(todayStr)),
                    })}
                  </p>
                </div>
              )}
              <div className="flex items-start gap-2 mb-2.5">
                <input
                  type="checkbox"
                  id="terms-cb"
                  checked={hasAcceptedTerms}
                  onChange={(e) => handleAcceptTermsChange(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 accent-primary-700 flex-shrink-0 cursor-pointer"
                />
                <label htmlFor="terms-cb" className="text-xs text-gray-700 leading-snug cursor-pointer">
                  <Trans
                    i18nKey="contractSign.consent"
                    ns="bessOnboarding"
                    values={{ companyName: contractData.company.name }}
                    components={{
                      strong: <strong className="font-semibold text-gray-900" />,
                      termsLink: <span />,
                    }}
                  />
                </label>
              </div>

              <button
                onClick={handleAcceptContract}
                disabled={!canSign}
                className={`w-full h-9 rounded-lg flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors ${
                  canSign
                    ? "bg-primary-700 hover:bg-primary-800 text-white"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing…
                  </>
                ) : canSign ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                    </svg>
                    Sign contract
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                    </svg>
                    {serverBlockedHint || controllerMissing ? "Cannot sign yet" : "Finish reading first"}
                  </>
                )}
              </button>

              {!canSign && !isProcessing && controllerHint && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.732 0 2.815-1.874 1.95-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                  <p className="text-xs text-amber-800 leading-relaxed">{controllerHint}</p>
                </div>
              )}

              {!canSign && !isProcessing && !controllerHint && serverBlockedHint && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.732 0 2.815-1.874 1.95-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                  <p className="text-xs text-amber-800 leading-relaxed">{serverBlockedHint}</p>
                </div>
              )}

              {!canSign && !isProcessing && !controllerHint && !serverBlockedHint && lockHint && (
                <p className="text-[11px] text-gray-400 text-center mt-1.5">{lockHint}</p>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};
